import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import Marquee from "react-fast-marquee";
import { 
  BrainCircuit, 
  Layers, 
  Share2, 
  Truck, 
  Gamepad2,
  Menu,
  X,
  ChevronDown,
  Linkedin,
  Twitter,
  Github,
  Mail,
  ArrowRight,
  Quote,
  Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Toaster, toast } from "sonner";
import { translations, languageNames } from "@/lib/i18n";
import { getFirebase, submitContactForm, subscribeNewsletter } from "@/lib/firebase";

// Initialize Firebase
getFirebase();

// Language Context
const LanguageContext = createContext();

const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};

const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState("en");
  const t = translations[language];
  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Media URLs from design guidelines
const media = {
  hero_bg: "https://images.unsplash.com/photo-1760224254103-3dcc15e4ad9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwdGVjaCUyMHRlY2hub2xvZ3klMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3NTczMTQyOHww&ixlib=rb-4.1.0&q=85",
  about_office: "/gis_photo.webp",
  team: [
    { url: "https://images.unsplash.com/photo-1762522927402-f390672558d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTczMTQxNnww&ixlib=rb-4.1.0&q=85", name: "Arslan Ceylan", title: "Founder & CEO" },
    { url: "https://images.unsplash.com/photo-1769636929261-e913ed023c83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTczMTQxNnww&ixlib=rb-4.1.0&q=85", name: "Elena Rostova", title: "Head of Emergent AI" },
    { url: "https://images.pexels.com/photos/14589344/pexels-photo-14589344.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940", name: "Marcus Chen", title: "VP of Logistics & CRM" },
  ],
  partner_logos: [
    "/eureka-logistics-logo.png",
    "/logo_letter_teal.png",
  ],
};

const testimonials = [
  {
    quote: "Alceylan's AI solutions transformed our operational efficiency by 40%. Their expertise in emergent AI technologies is unparalleled.",
    author: "Sarah Mitchell",
    role: "CTO, TechVentures Inc.",
  },
  {
    quote: "The custom ERP system they built for us seamlessly integrates with our global logistics network. Exceptional quality and support.",
    author: "Michael Tanaka",
    role: "Operations Director, GlobalTrade Co.",
  },
  {
    quote: "Joinzy has revolutionized how our community engages. The platform's innovation and user experience are outstanding.",
    author: "Lisa Chen",
    role: "Community Manager, StartupHub",
  },
];

// Team data with translation keys
const teamKeys = ['ceo', 'ai', 'logistics'];
const teamImages = [
  "https://images.unsplash.com/photo-1762522927402-f390672558d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTczMTQxNnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1769636929261-e913ed023c83?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHw0fHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBoZWFkc2hvdCUyMHBvcnRyYWl0fGVufDB8fHx8MTc3NTczMTQxNnww&ixlib=rb-4.1.0&q=85",
  "https://images.pexels.com/photos/14589344/pexels-photo-14589344.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
];

// Scroll helper function with offset for fixed header
const scrollToSection = (sectionId) => {
  const element = document.getElementById(sectionId);
  if (element) {
    const headerOffset = 100;
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

// Header Component
const Header = () => {
  const { language, setLanguage, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: t.nav.services, href: "services" },
    { label: t.nav.about, href: "about" },
    { label: t.nav.team, href: "team" },
    { label: t.nav.testimonials, href: "testimonials" },
    { label: t.nav.contact, href: "contact" },
  ];

  return (
    <header className={`header-nav ${scrolled ? "scrolled" : ""}`} data-testid="main-header">
      <div className="section-container">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#" className="flex items-center gap-2" data-testid="logo" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>
            <img src="/logo.png" alt="Alceylan Enterprises" className="h-14 w-auto" />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollToSection(link.href)}
                className="text-[#94A3B8] hover:text-[#F0F4F8] transition-colors text-sm font-medium bg-transparent border-none cursor-pointer"
                data-testid={`nav-link-${link.href}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger 
                className="w-[100px] bg-transparent border-[#4A6D7C]/30 text-[#F0F4F8] focus:ring-[#7291B9]"
                data-testid="language-switcher"
              >
                <Globe className="w-4 h-4 mr-1 text-[#7291B9]" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#222B3A] border-[#4A6D7C]/30">
                {Object.entries(languageNames).map(([code, name]) => (
                  <SelectItem 
                    key={code} 
                    value={code}
                    className="text-[#F0F4F8] focus:bg-[#7291B9]/20 focus:text-[#F0F4F8]"
                    data-testid={`language-option-${code}`}
                  >
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* CTA Button */}
            <Button 
              className="btn-primary hidden md:block" 
              data-testid="header-cta-button"
              onClick={() => scrollToSection('services')}
            >
              {t.nav.exploreSolutions}
            </Button>

            {/* Mobile Menu Toggle */}
            <button
              className="lg:hidden p-2 text-[#F0F4F8]"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.nav
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden pb-6"
              data-testid="mobile-nav"
            >
              {navLinks.map((link) => (
                <button
                  key={link.href}
                  onClick={() => {
                    scrollToSection(link.href);
                    setMobileMenuOpen(false);
                  }}
                  className="block w-full text-left py-3 text-[#94A3B8] hover:text-[#F0F4F8] transition-colors bg-transparent border-none cursor-pointer"
                >
                  {link.label}
                </button>
              ))}
              <Button 
                className="btn-primary w-full mt-4"
                onClick={() => {
                  scrollToSection('services');
                  setMobileMenuOpen(false);
                }}
              >
                {t.nav.exploreSolutions}
              </Button>
            </motion.nav>
          )}
        </AnimatePresence>
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section className="hero-section" data-testid="hero-section">
      <div className="hero-bg">
        <img src={media.hero_bg} alt="Technology Background" loading="eager" />
      </div>
      
      <div className="section-container relative z-10 pt-20">
        <div className="max-w-4xl">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#F0F4F8] tracking-tight font-[Outfit] leading-tight"
            data-testid="hero-headline"
          >
            {t.hero.headline}
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-6 text-lg md:text-xl text-[#94A3B8] max-w-2xl leading-relaxed"
            data-testid="hero-subtext"
          >
            {t.hero.subtext}
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Button 
              className="btn-primary flex items-center gap-2" 
              data-testid="hero-explore-btn"
              onClick={() => scrollToSection('services')}
            >
              {t.hero.exploreSolutions}
              <ArrowRight className="w-4 h-4" />
            </Button>
            <Button 
              className="btn-secondary" 
              data-testid="hero-partner-btn"
              onClick={() => scrollToSection('contact')}
            >
              {t.hero.partnerWithUs}
            </Button>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const { t } = useLanguage();

  const services = [
    { key: "logistics", icon: Truck, span: "col-span-12 md:col-span-8" },
    { key: "saas", icon: Layers, span: "col-span-12 md:col-span-4" },
    { key: "joinzy", icon: Share2, span: "col-span-12 md:col-span-4" },
    { key: "gaming", icon: Gamepad2, span: "col-span-12 md:col-span-4" },
  ];

  return (
    <section id="services" className="section-padding bg-[#1A222E]" data-testid="services-section">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="overline" data-testid="services-overline">{t.services.overline}</span>
          <h2 className="mt-4 text-3xl md:text-4xl font-medium text-[#F0F4F8] font-[Outfit] tracking-tight" data-testid="services-title">
            {t.services.title}
          </h2>
        </motion.div>

        <div className="bento-grid">
          {services.map((service, index) => {
            const Icon = service.icon;
            const svc = t.services[service.key];
            return (
              <motion.div
                key={service.key}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`service-card ${service.span}`}
                data-testid={`service-card-${service.key}`}
              >
                <div className="service-icon">
                  <Icon className="w-7 h-7 text-[#7291B9]" />
                </div>
                <h3 className="text-xl md:text-2xl font-medium text-[#F0F4F8] font-[Outfit] mb-3">
                  {svc.title}
                </h3>
                <p className="text-[#94A3B8] leading-relaxed">
                  {svc.description}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t } = useLanguage();

  return (
    <section id="about" className="section-padding bg-[#222B3A]" data-testid="about-section">
      <div className="section-container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="overline" data-testid="about-overline">{t.about.overline}</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-medium text-[#F0F4F8] font-[Outfit] tracking-tight" data-testid="about-title">
              {t.about.title}
            </h2>
            <div className="mt-8 space-y-6 text-[#94A3B8] leading-relaxed">
              <p>{t.about.p1}</p>
              <p>{t.about.p2}</p>
              <p>{t.about.p3}</p>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="rounded-2xl overflow-hidden border border-[#4A6D7C]/20">
              <img 
                src={media.about_office} 
                alt="Modern Tech Office" 
                className="w-full h-full object-cover"
                loading="lazy"
                data-testid="about-image"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 w-32 h-32 rounded-xl bg-[#7291B9]/10 border border-[#7291B9]/30 -z-10" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Team Section
const TeamSection = () => {
  const { t } = useLanguage();

  return (
    <section id="team" className="section-padding bg-[#1A222E]" data-testid="team-section" style={{ display: 'none' }}>
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="overline" data-testid="team-overline">{t.team.overline}</span>
          <h2 className="mt-4 text-3xl md:text-4xl font-medium text-[#F0F4F8] font-[Outfit] tracking-tight" data-testid="team-title">
            {t.team.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {teamKeys.map((key, index) => (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
              className="team-member group text-center"
              data-testid={`team-member-${index}`}
            >
              <div className="relative overflow-hidden rounded-2xl mb-6">
                <img
                  src={teamImages[index]}
                  alt={t.team.members[key].name}
                  className="team-image w-full"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A222E]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              <h3 className="text-xl font-medium text-[#F0F4F8] font-[Outfit]">
                {t.team.members[key].name}
              </h3>
              <p className="text-[#94A3B8] mt-1">{t.team.members[key].title}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const { t } = useLanguage();

  return (
    <section id="testimonials" className="section-padding bg-[#222B3A]" data-testid="testimonials-section">
      <div className="section-container">
        {/* Partners Marquee */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <p className="text-center text-sm text-[#94A3B8] mb-8">{t.testimonials.partners}</p>
          <div className="marquee-container">
            <Marquee gradient={false} speed={40} pauseOnHover data-testid="partners-marquee">
              {[...media.partner_logos, ...media.partner_logos].map((logo, index) => (
                <div key={index} className="mx-12">
                  <img 
                    src={logo} 
                    alt={`Partner ${index + 1}`} 
                    className="partner-logo"
                    loading="lazy"
                  />
                </div>
              ))}
            </Marquee>
          </div>
        </motion.div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="overline" data-testid="testimonials-overline">{t.testimonials.overline}</span>
          <h2 className="mt-4 text-3xl md:text-4xl font-medium text-[#F0F4F8] font-[Outfit] tracking-tight" data-testid="testimonials-title">
            {t.testimonials.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {t.testimonials.items.map((testimonial, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="testimonial-card"
              data-testid={`testimonial-card-${index}`}
            >
              <Quote className="w-8 h-8 text-[#7291B9] mb-4" />
              <p className="text-[#F0F4F8] leading-relaxed mb-6">
                "{testimonial.quote}"
              </p>
              <div className="border-t border-[#4A6D7C]/20 pt-4">
                <p className="font-medium text-[#F0F4F8]">{testimonial.author}</p>
                <p className="text-sm text-[#94A3B8]">{testimonial.role}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t } = useLanguage();
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    service: "",
    message: "",
  });
  const [formLoading, setFormLoading] = useState(false);

  const serviceOptions = [
    { value: "ai", label: t.services.ai.title },
    { value: "saas", label: t.services.saas.title },
    { value: "joinzy", label: t.services.joinzy.title },
    { value: "logistics", label: t.services.logistics.title },
    { value: "gaming", label: t.services.gaming.title },
  ];

  const handleNewsletterSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!newsletterEmail) return;
    
    setNewsletterLoading(true);
    const result = await subscribeNewsletter(newsletterEmail);
    setNewsletterLoading(false);
    
    if (result.success) {
      toast.success(t.contact.newsletter.success);
      setNewsletterEmail("");
    } else {
      toast.error(result.error || "Failed to subscribe");
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    
    setFormLoading(true);
    const result = await submitContactForm(formData);
    setFormLoading(false);
    
    if (result.success) {
      toast.success(t.contact.form.success);
      setFormData({ name: "", email: "", service: "", message: "" });
    } else {
      toast.error(t.contact.form.error);
    }
  };

  return (
    <section id="contact" className="section-padding bg-[#1A222E]" data-testid="contact-section">
      <div className="section-container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="overline" data-testid="contact-overline">{t.contact.overline}</span>
          <h2 className="mt-4 text-3xl md:text-4xl font-medium text-[#F0F4F8] font-[Outfit] tracking-tight" data-testid="contact-title">
            {t.contact.title}
          </h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Newsletter */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="p-8 rounded-2xl bg-[#222B3A] border border-[#4A6D7C]/20">
              <h3 className="text-2xl font-medium text-[#F0F4F8] font-[Outfit] mb-2" data-testid="newsletter-title">
                {t.contact.newsletter.title}
              </h3>
              <p className="text-[#94A3B8] mb-6">
                {t.contact.newsletter.description}
              </p>
              <div className="flex gap-3">
                <Input
                  type="email"
                  placeholder={t.contact.newsletter.placeholder}
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleNewsletterSubmit()}
                  className="form-input flex-1"
                  data-testid="newsletter-email-input"
                />
                <Button
                  type="button"
                  className="btn-primary"
                  disabled={newsletterLoading}
                  onClick={handleNewsletterSubmit}
                  data-testid="newsletter-submit-button"
                >
                  {newsletterLoading ? "..." : t.contact.newsletter.button}
                </Button>
              </div>
            </div>

            {/* Social Links */}
            <div className="mt-8 flex gap-4">
              <a 
                href="https://linkedin.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-lg bg-[#222B3A] border border-[#4A6D7C]/20 flex items-center justify-center text-[#94A3B8] hover:text-[#7291B9] hover:border-[#7291B9]/50 transition-all"
                data-testid="social-linkedin"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a 
                href="https://twitter.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-lg bg-[#222B3A] border border-[#4A6D7C]/20 flex items-center justify-center text-[#94A3B8] hover:text-[#7291B9] hover:border-[#7291B9]/50 transition-all"
                data-testid="social-twitter"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a 
                href="https://github.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-12 h-12 rounded-lg bg-[#222B3A] border border-[#4A6D7C]/20 flex items-center justify-center text-[#94A3B8] hover:text-[#7291B9] hover:border-[#7291B9]/50 transition-all"
                data-testid="social-github"
              >
                <Github className="w-5 h-5" />
              </a>
              <a 
                href="mailto:contact@alceylan.com"
                className="w-12 h-12 rounded-lg bg-[#222B3A] border border-[#4A6D7C]/20 flex items-center justify-center text-[#94A3B8] hover:text-[#7291B9] hover:border-[#7291B9]/50 transition-all"
                data-testid="social-email"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form onSubmit={handleFormSubmit} className="space-y-6">
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">{t.contact.form.name}</label>
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input"
                  data-testid="contact-name-input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">{t.contact.form.email}</label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="form-input"
                  data-testid="contact-email-input"
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">{t.contact.form.service}</label>
                <Select 
                  value={formData.service} 
                  onValueChange={(value) => setFormData({ ...formData, service: value })}
                >
                  <SelectTrigger 
                    className="form-input"
                    data-testid="contact-service-select"
                  >
                    <SelectValue placeholder={t.contact.form.selectService} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#222B3A] border-[#4A6D7C]/30">
                    {serviceOptions.map((option) => (
                      <SelectItem 
                        key={option.value} 
                        value={option.value}
                        className="text-[#F0F4F8] focus:bg-[#7291B9]/20 focus:text-[#F0F4F8]"
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm text-[#94A3B8] mb-2">{t.contact.form.message}</label>
                <Textarea
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="form-input min-h-[120px] resize-none"
                  data-testid="contact-message-input"
                  required
                />
              </div>
              <Button 
                type="submit" 
                className="btn-primary w-full"
                disabled={formLoading}
                data-testid="contact-submit-button"
              >
                {formLoading ? t.contact.form.sending : t.contact.form.submit}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { t } = useLanguage();
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  return (
    <>
      <footer className="footer bg-[#1A222E]" data-testid="footer">
        <div className="section-container">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Alceylan Enterprises" className="h-8 w-auto" />
              <span className="text-[#94A3B8] text-sm">
                {t.footer.copyright}
              </span>
            </div>
            <div className="flex gap-6 text-sm text-[#94A3B8]">
              <button 
                onClick={() => setPrivacyOpen(true)} 
                className="hover:text-[#F0F4F8] transition-colors bg-transparent border-none cursor-pointer" 
                data-testid="privacy-link"
              >
                {t.footer.privacy}
              </button>
              <button 
                onClick={() => setTermsOpen(true)} 
                className="hover:text-[#F0F4F8] transition-colors bg-transparent border-none cursor-pointer" 
                data-testid="terms-link"
              >
                {t.footer.terms}
              </button>
            </div>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <Dialog open={privacyOpen} onOpenChange={setPrivacyOpen}>
        <DialogContent className="max-w-3xl bg-[#1A222E] border-[#4A6D7C]/30 text-[#F0F4F8]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-[Outfit] text-[#F0F4F8]">{t.footer.privacy}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-6 text-[#94A3B8] text-sm leading-relaxed">
              <p className="text-xs text-[#7291B9]">{t.legal.lastUpdated}: January 2025</p>
              
              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.privacy.intro.title}</h3>
                <p>{t.legal.privacy.intro.content}</p>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.privacy.collection.title}</h3>
                <p>{t.legal.privacy.collection.content}</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  {t.legal.privacy.collection.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.privacy.usage.title}</h3>
                <p>{t.legal.privacy.usage.content}</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  {t.legal.privacy.usage.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.privacy.security.title}</h3>
                <p>{t.legal.privacy.security.content}</p>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.privacy.cookies.title}</h3>
                <p>{t.legal.privacy.cookies.content}</p>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.privacy.rights.title}</h3>
                <p>{t.legal.privacy.rights.content}</p>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.privacy.contact.title}</h3>
                <p>{t.legal.privacy.contact.content}</p>
                <p className="mt-2 text-[#7291B9]">veyselalceylan@alceylanenterprises.com</p>
              </section>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Terms of Service Modal */}
      <Dialog open={termsOpen} onOpenChange={setTermsOpen}>
        <DialogContent className="max-w-3xl bg-[#1A222E] border-[#4A6D7C]/30 text-[#F0F4F8]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-[Outfit] text-[#F0F4F8]">{t.footer.terms}</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[70vh] pr-4">
            <div className="space-y-6 text-[#94A3B8] text-sm leading-relaxed">
              <p className="text-xs text-[#7291B9]">{t.legal.lastUpdated}: January 2025</p>
              
              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.terms.acceptance.title}</h3>
                <p>{t.legal.terms.acceptance.content}</p>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.terms.services.title}</h3>
                <p>{t.legal.terms.services.content}</p>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.terms.intellectual.title}</h3>
                <p>{t.legal.terms.intellectual.content}</p>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.terms.liability.title}</h3>
                <p>{t.legal.terms.liability.content}</p>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.terms.confidentiality.title}</h3>
                <p>{t.legal.terms.confidentiality.content}</p>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.terms.termination.title}</h3>
                <p>{t.legal.terms.termination.content}</p>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.terms.governing.title}</h3>
                <p>{t.legal.terms.governing.content}</p>
              </section>

              <section>
                <h3 className="text-lg font-medium text-[#F0F4F8] mb-2">{t.legal.terms.contact.title}</h3>
                <p>{t.legal.terms.contact.content}</p>
                <p className="mt-2 text-[#7291B9]">veyselalceylan@alceylanenterprises.com</p>
              </section>
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Main App
function App() {
  return (
    <LanguageProvider>
      <div className="min-h-screen bg-[#1A222E]">
        <Header />
        <main>
          <HeroSection />
          <ServicesSection />
          <AboutSection />
          <TeamSection />
          <TestimonialsSection />
          <ContactSection />
        </main>
        <Footer />
        <Toaster 
          position="top-right" 
          toastOptions={{
            style: {
              background: '#222B3A',
              color: '#F0F4F8',
              border: '1px solid rgba(114, 145, 185, 0.3)',
            },
          }}
        />
      </div>
    </LanguageProvider>
  );
}

export default App;
